import { Button } from '@mantine/core';
import { IconCalendarEvent } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import type { Upcoming } from '../../shared/upcoming';
import { sessionAt, TIME_ZONE } from '../../shared/format';
import { classNames } from '../classNames';
import { upcomingQuery } from '../queries';
import {
  COMBINED,
  IMAGES,
  NOTICES,
  type CombinedTournaments,
  type Notice,
} from './eventPosters';
import { Link } from './Link';

const COPY = {
  title: 'Coming up',
  learnMore: 'Learn more',
  levelDate: (level: string, date: string) => `${level} · ${date}`,
  opens: (on: string) => `Registration opens ${on}`,
};

export interface Poster {
  key: string;
  href: string | null;
  title: string;
  lines: string[];
  startsAt: number;
  endsAt: number;
  image: string | undefined;
  linkLabel: string | null;
  /** `opensOn` is null once registration is open. */
  feature?: { headline: string; body: string; opensOn: string | null };
}

const CONFIG = { images: IMAGES, combined: COMBINED, notices: NOTICES };

type PosterConfig = typeof CONFIG;

const HOUR = 60 * 60 * 1000;
// Events carry no end time, so an evening event stays listed that night.
const EVENT_SHOWN_FOR = 12 * HOUR;

const monthFormatter = new Intl.DateTimeFormat('en-CA', {
  month: 'short',
  timeZone: TIME_ZONE,
});
const dayFormatter = new Intl.DateTimeFormat('en-CA', {
  day: 'numeric',
  timeZone: TIME_ZONE,
});
const dateFormatter = new Intl.DateTimeFormat('en-CA', {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
  timeZone: TIME_ZONE,
});

const local = (date: string, time: string) =>
  Date.parse(`${date}T${time}:00-07:00`);

function joined(...parts: string[]): string {
  return parts.filter(Boolean).join(' · ');
}

function eventPoster(
  event: Upcoming['events'][number],
  config: PosterConfig,
): Poster {
  const startsAt = Date.parse(event.startsAt!);
  return {
    key: event.id,
    href: `/events/${event.id}`,
    title: event.name,
    lines: [joined(sessionAt(startsAt), event.location)],
    startsAt,
    endsAt: startsAt + EVENT_SHOWN_FOR,
    image: config.images[event.id],
    linkLabel: COPY.learnMore,
  };
}

function tournamentPoster(
  tournament: Upcoming['tournaments'][number],
  config: PosterConfig,
): Poster {
  const startsAt = local(tournament.startsOn, '12:00');
  return {
    key: tournament.id,
    href: `/tournaments/${tournament.id}`,
    title: tournament.name,
    lines: [joined(dateFormatter.format(startsAt), tournament.venue)],
    startsAt,
    endsAt: local(tournament.startsOn, '23:59'),
    image: config.images[tournament.id],
    linkLabel: COPY.learnMore,
  };
}

function combinedPoster(
  group: CombinedTournaments,
  tournaments: Upcoming['tournaments'],
): Poster {
  const sorted = [...tournaments].sort((a, b) =>
    a.startsOn.localeCompare(b.startsOn),
  );
  const first = sorted[0]!;
  const venues = [...new Set(sorted.map((each) => each.venue))];
  return {
    key: group.title,
    href: group.link?.href ?? '/tournaments',
    title: group.title,
    lines: [
      ...sorted.map((each) =>
        COPY.levelDate(
          each.level,
          dateFormatter.format(local(each.startsOn, '12:00')),
        ),
      ),
      ...(venues.length === 1 ? venues : []),
    ],
    startsAt: local(first.startsOn, '12:00'),
    endsAt: local(sorted.at(-1)!.startsOn, '23:59'),
    image: group.image,
    linkLabel: group.link?.label ?? COPY.learnMore,
    ...(group.feature
      ? {
          feature: {
            ...group.feature,
            opensOn: tournaments.some(
              (each) => each.registrationState === 'Open',
            )
              ? null
              : group.feature.opensOn,
          },
        }
      : {}),
  };
}

function noticePoster(notice: Notice): Poster {
  const startsAt = local(notice.on, notice.time ?? '12:00');
  return {
    key: `${notice.on}:${notice.title}`,
    href: notice.link?.href ?? null,
    title: notice.title,
    lines: [notice.time ? sessionAt(startsAt) : dateFormatter.format(startsAt)],
    startsAt,
    endsAt: notice.time ? startsAt : local(notice.on, '23:59'),
    image: notice.image,
    linkLabel: notice.link?.label ?? null,
  };
}

export function upcomingPosters(
  events: Upcoming['events'],
  tournaments: Upcoming['tournaments'],
  now: number,
  config: PosterConfig = CONFIG,
): Poster[] {
  const grouped = new Set(
    config.combined.flatMap((group) => group.tournaments),
  );
  const combined = config.combined.flatMap((group) => {
    const members = tournaments.filter((each) =>
      group.tournaments.includes(each.id),
    );
    return members.length ? [combinedPoster(group, members)] : [];
  });
  return [
    ...events
      .filter((event) => event.startsAt !== null)
      .map((event) => eventPoster(event, config)),
    ...tournaments
      .filter((each) => !grouped.has(each.id))
      .map((each) => tournamentPoster(each, config)),
    ...combined,
    ...config.notices.map(noticePoster),
  ]
    .filter((poster) => poster.endsAt >= now)
    .sort(
      (a, b) =>
        Number(!!b.feature) - Number(!!a.feature) || a.startsAt - b.startsAt,
    );
}

const CARD =
  'border-hairline bg-surface block shrink-0 overflow-hidden rounded-xl border';

function PosterBody({ poster }: { poster: Poster }) {
  return (
    <>
      {poster.image ? (
        <img src={poster.image} alt="" className="h-20 w-full object-cover" />
      ) : null}
      <div className="flex gap-3 p-3">
        <div className="bg-cream border-hairline flex size-12 shrink-0 flex-col items-center justify-center self-start rounded-lg border leading-none">
          <span className="text-attention-text text-[11px] font-medium uppercase">
            {monthFormatter.format(poster.startsAt)}
          </span>
          <span className="font-display text-ink text-xl font-bold">
            {dayFormatter.format(poster.startsAt)}
          </span>
        </div>
        <div className="flex min-w-0 grow flex-col gap-1.5">
          <div className="min-w-0">
            <p className="text-ink text-sm font-medium">{poster.title}</p>
            {poster.lines.map((line) => (
              <p key={line} className="text-muted text-xs">
                {line}
              </p>
            ))}
          </div>
          {poster.linkLabel ? (
            <span className="text-link self-end text-xs font-medium whitespace-nowrap">
              {poster.linkLabel}
            </span>
          ) : null}
        </div>
      </div>
    </>
  );
}

function FeaturedPoster({
  poster,
  feature,
}: {
  poster: Poster;
  feature: NonNullable<Poster['feature']>;
}) {
  return (
    <article
      aria-label={poster.title}
      className="border-hairline bg-surface shrink-0 overflow-hidden rounded-xl border"
    >
      {poster.image ? (
        <img src={poster.image} alt="" className="h-24 w-full object-cover" />
      ) : null}
      <div className="flex flex-col gap-3 p-4 text-center">
        <p className="text-ink text-lg font-bold text-balance">
          {feature.headline}
        </p>
        <p className="text-body text-sm">{feature.body}</p>
        {feature.opensOn ? (
          <p className="bg-gold-soft text-int-ink flex items-center justify-center gap-2 rounded-md py-2 font-medium">
            <IconCalendarEvent size={18} aria-hidden />
            {COPY.opens(feature.opensOn)}
          </p>
        ) : (
          <Button component={Link} href={poster.href!} fullWidth>
            {poster.linkLabel}
          </Button>
        )}
      </div>
    </article>
  );
}

function PosterCard({ poster }: { poster: Poster }) {
  if (poster.feature)
    return <FeaturedPoster poster={poster} feature={poster.feature} />;
  if (!poster.href)
    return (
      <article className={CARD}>
        <PosterBody poster={poster} />
      </article>
    );
  const className = classNames(
    CARD,
    'focus-visible:outline-brand-dark transition-shadow hover:shadow-md focus-visible:outline-2 focus-visible:outline-offset-2',
  );
  if (/^https?:/.test(poster.href))
    return (
      <a
        href={poster.href}
        target="_blank"
        rel="noreferrer"
        className={className}
      >
        <PosterBody poster={poster} />
      </a>
    );
  return (
    <Link href={poster.href} className={className}>
      <PosterBody poster={poster} />
    </Link>
  );
}

// Fades an edge while more posters sit past it.
function PosterList({ posters }: { posters: Poster[] }) {
  const list = useRef<HTMLDivElement>(null);
  const [more, setMore] = useState({ above: false, below: false });
  useEffect(() => {
    const element = list.current!;
    const update = () =>
      setMore({
        above: element.scrollTop > 1,
        below:
          element.scrollTop < element.scrollHeight - element.clientHeight - 1,
      });
    update();
    const resized = new ResizeObserver(update);
    for (const card of element.children) resized.observe(card);
    resized.observe(element);
    element.addEventListener('scroll', update);
    return () => {
      resized.disconnect();
      element.removeEventListener('scroll', update);
    };
  }, [posters]);
  const fade = (hidden: boolean) => (hidden ? 'transparent' : 'black');
  return (
    <div
      ref={list}
      className="flex min-h-0 [scrollbar-width:thin] flex-col gap-3 overflow-y-auto pr-1"
      style={{
        maskImage: `linear-gradient(to bottom, ${fade(more.above)}, black 2.5rem, black calc(100% - 4rem), ${fade(more.below)})`,
      }}
    >
      {posters.map((poster) => (
        <PosterCard key={poster.key} poster={poster} />
      ))}
    </div>
  );
}

export function EventPosterWall({
  showEvents,
  showTournaments,
}: {
  showEvents: boolean;
  showTournaments: boolean;
}) {
  const upcoming = useQuery({
    ...upcomingQuery(),
    enabled: showEvents || showTournaments,
  });
  const posters = upcomingPosters(
    showEvents ? (upcoming.data?.events ?? []) : [],
    showTournaments ? (upcoming.data?.tournaments ?? []) : [],
    Date.now(),
  );
  if (!posters.length) return null;
  return (
    <section
      aria-labelledby="event-posters-title"
      className="flex min-h-0 w-full max-w-72 flex-col gap-3"
    >
      <h2
        id="event-posters-title"
        className="font-display text-ink text-sm font-bold tracking-wide uppercase"
      >
        {COPY.title}
      </h2>
      <PosterList posters={posters} />
    </section>
  );
}
